import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Col,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Row,
  Switch,
  useToast,
} from "@ir/ui";
import { CreatePurchaserDTO } from "@src/domain/dto/purchasers";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import {
  ENTITY_KEY_MAX_LENGTH,
  generateEntityKey,
  getInputValue,
  sanitizeKeyInput,
} from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import {
  formatPurchaserKey,
  getPurchaserKeyFieldError,
} from "./purchaserKeyUtils";

const PURCHASER_KEY_CHARS = /[^A-Z0-9-]/g;

interface AddPurchaserRowProps {
  onCancel: () => void;
  onCreated: () => void;
}

interface AddPurchaserFormValues {
  purchaserName: string;
  purchaserKey: string;
}

export const AddPurchaserRow = ({
  onCancel,
  onCreated,
}: AddPurchaserRowProps) => {
  const { createPurchaser, applyPurchaserCreate, setPage, page } =
    usePurchasersData();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<AddPurchaserFormValues>();
  const [isActive, setIsActive] = useState(true);
  const [isKeyEditedManually, setIsKeyEditedManually] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const purchaserNameRules = useMemo(
    () => [
      { required: true, message: "Purchaser name is required" },
      {
        max: 100,
        message: "Purchaser name must not exceed 100 characters",
      },
    ],
    [],
  );

  const purchaserKeyRules = useMemo(
    () => [
      { required: true, message: "Purchaser key is required" },
      {
        pattern: /^[A-Z0-9]+(-[A-Z0-9]+)*$/,
        message:
          "Only uppercase letters and hyphens are allowed",
      },
    ],
    [],
  );

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);
    const values = form.getFieldsValue();
    const hasRequiredValues =
      (values.purchaserName?.trim() ?? "") !== "" &&
      (values.purchaserKey?.trim() ?? "") !== "";
    setIsSubmittable(hasRequiredValues && !hasErrors);
  }, [form]);

  const syncPurchaserKeyFromName = (valueOrEvent: unknown) => {
    if (isKeyEditedManually) return;
    form.setFields([
      {
        name: "purchaserKey",
        errors: [],
      },
    ]);
    form.setFieldsValue({
      purchaserKey: generateEntityKey(
        getInputValue(valueOrEvent),
        PURCHASER_KEY_CHARS,
        ENTITY_KEY_MAX_LENGTH,
      ),
    });
    form.validateFields(["purchaserKey"]).catch(() => {});
  };

  const handlePurchaserNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      purchaserName: getInputValue(valueOrEvent),
    });
    syncPurchaserKeyFromName(valueOrEvent);
    form.validateFields(["purchaserName"]).catch(() => {});
  };

  const handleSave = async (values: AddPurchaserFormValues) => {
    setIsSaving(true);
    try {
      const nextName = values.purchaserName.trim();
      const nextKey = formatPurchaserKey(values.purchaserKey);
      const validated = CreatePurchaserDTO.validate({
        purchaserName: nextName,
        purchaserKey: nextKey,
        isActive,
      });
      if (!validated.success) {
        throw new Error("Validation failed");
      }

      const created = await createPurchaser(
        new CreatePurchaserDTO(validated.data),
      );
      successToast({
        content: `Purchaser '${created.purchaserName}' added successfully!`,
        icon: <Icon name={Icons.CheckCircle} />,
      });
      applyPurchaserCreate(created);
      if (page !== 1) {
        setPage(1);
      }
      onCreated();
    } catch (error) {
      const keyFieldError = getPurchaserKeyFieldError(error);
      if (keyFieldError) {
        form.setFields([
          {
            name: "purchaserKey",
            errors: [keyFieldError],
          },
        ]);
        setIsSubmittable(false);
      } else {
        console.error("Failed to create purchaser", error);
        errorToast({
          content: "Failed to create purchaser. Please try again.",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="bg-white shadow-sm border-1 border-color-grey-25 rounded-medium mb-2">
      <CardContent className="p-3">
        <Form<AddPurchaserFormValues>
          form={form}
          onFinish={handleSave}
          onFieldsChange={updateFormValidity}
          initialValues={{ purchaserName: "", purchaserKey: "" }}
          className="purchaser-form"
        >
          <Flex align="center" justifyContent="space-between" gap={1}>
            <Row style={{ flex: 1 }}>
              <Col span={6}>
                <Form.Item
                  name="purchaserName"
                  validateTrigger={["onChange", "onBlur"]}
                  rules={purchaserNameRules}
                >
                  <Input
                    placeholder="Enter Purchaser Name"
                    maxLength={100}
                    onChange={handlePurchaserNameChange}
                    onInput={handlePurchaserNameChange}
                    onKeyUp={handlePurchaserNameChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="purchaserKey"
                  validateTrigger={["onChange", "onBlur"]}
                  rules={purchaserKeyRules}
                >
                  <Input
                    placeholder="KEY - UPPERCASE-WITH-DASHES"
                    onChange={(valueOrEvent) => {
                      setIsKeyEditedManually(true);
                      form.setFields([
                        {
                          name: "purchaserKey",
                          errors: [],
                        },
                      ]);
                      form.setFieldsValue({
                        purchaserKey: sanitizeKeyInput(
                          getInputValue(valueOrEvent),
                          PURCHASER_KEY_CHARS,
                        ),
                      });
                      form.validateFields(["purchaserKey"]).catch(() => {});
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Flex align="center" gap={0.5}>
              <Switch
                size="small"
                isSelected={isActive}
                onChange={setIsActive}
                isDisabled={isSaving}
              />
              <Button
                size="small"
                color="primary"
                variant="clear"
                onPress={() => form.submit()}
                isLoading={isSaving}
                isDisabled={isSaving || !isSubmittable}
              >
                <Icon name={Icons.Check} />
              </Button>
              <Button
                size="small"
                color="grey"
                variant="clear"
                onPress={onCancel}
                isDisabled={isSaving}
              >
                <Icon name={Icons.Close} />
              </Button>
            </Flex>
          </Flex>
        </Form>
      </CardContent>
    </Card>
  );
};
